import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Stage, Layer, Rect, Line } from 'react-konva'
import FloatingToolbar from './FloatingToolbar'
import FixedToolbar from './FixedToolbar'
import TransformableText from './TransformableText'
import TransformableImage from './TransformableImage'
import useHistory from './useHistory'
import { getCanvasDimensions, isImageField, isQrField, isLogoField, createTextField, enforcePhotoRatio } from './canvasUtils'
import styles from './KonvaCanvas.module.scss'

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 2.5]

export default function KonvaCanvas({
  fields,
  setFields,
  imageUrl,
  template,
  onSelectField,
  fonts = [],
  maxContainerWidth = null,
  maxContainerHeight = null,
  hideBoundingBoxes: hideBoundingBoxesProp = null,
  onHideBoundingBoxesChange = null,
  showControls = true,
  showFixedToolbar = false,
  showZoomControls = false,
  showSafetyZone: showSafetyZoneProp = false,
  onSafetyZoneChange = null,
  onSpellCheck = null,
  onAddText = null,
  onOverflowChange = null,
  onExportReady = null,
}) {
  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [editingField, setEditingField] = useState(null)
  const [editText, setEditText] = useState('')
  const [hideBoundingBoxesLocal, setHideBoundingBoxesLocal] = useState(false)

  // Allow controlled or uncontrolled mode for hideBoundingBoxes
  const hideBoundingBoxes = hideBoundingBoxesProp !== null ? hideBoundingBoxesProp : hideBoundingBoxesLocal
  const setHideBoundingBoxes = onHideBoundingBoxesChange || setHideBoundingBoxesLocal

  // Toolbar and clipboard state
  const [toolbarPosition, setToolbarPosition] = useState({ x: 100, y: 100 })
  const [copiedField, setCopiedField] = useState(null)
  const [copiedFormatting, setCopiedFormatting] = useState(null)
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, fieldId: null, pastePosition: null })
  const [displayScale, setDisplayScale] = useState(1)

  // Zoom state
  const [userZoom, setUserZoom] = useState(1.0)
  const effectiveScale = displayScale * userZoom

  // Toast notification state
  const [toast, setToast] = useState({ visible: false, message: '' })

  // Rubber-band selection state
  const [selectionRect, setSelectionRect] = useState(null)
  const isSelectingRef = useRef(false)

  // Snap guides state
  const [snapGuides, setSnapGuides] = useState({ vertical: null, horizontal: null })

  // Safety zone state (allow controlled or uncontrolled)
  const [safetyZoneLocal, setSafetyZoneLocal] = useState(showSafetyZoneProp)
  const showSafetyZone = onSafetyZoneChange ? showSafetyZoneProp : safetyZoneLocal
  const setShowSafetyZone = onSafetyZoneChange || setSafetyZoneLocal

  // Group move refs
  const fieldGroupRefs = useRef({})
  const dragStartPositions = useRef({})

  // Refs
  const stageRef = useRef()
  const canvasWrapperRef = useRef()
  const containerRef = useRef()
  const textInputRef = useRef()
  const editTextRef = useRef('')
  const positionedForSelectionRef = useRef(null)

  // Calculate canvas dimensions from template
  const dimensions = getCanvasDimensions(
    template.scaling_factor,
    template.image_width,
    template.image_height
  )

  // Derived selection helpers
  const primarySelectedId = selectedIds.size > 0 ? [...selectedIds][0] : null
  const selectedField = primarySelectedId ? fields[primarySelectedId] : null

  // Toast notification helper
  const showToast = useCallback((message, duration = 2000) => {
    setToast({ visible: true, message })
    setTimeout(() => setToast({ visible: false, message: '' }), duration)
  }, [])

  // Undo/Redo using custom hook
  const { handleUndo, handleRedo, canUndo, canRedo } = useHistory(fields, setFields, showToast)

  // Force-load fonts used by fields, then redraw canvas
  // Canvas text doesn't trigger browser font loading, so fonts must be loaded explicitly.
  // Many fonts from S3 don't have matching @font-face CSS rules, so we register them
  // dynamically using the FontFace API before calling document.fonts.load().
  const [fontsReady, setFontsReady] = useState(false)
  const registeredFontsRef = useRef(new Set())
  useEffect(() => {
    const fontNames = [...new Set(Object.values(fields).map(f => f.fontName).filter(Boolean))]
    if (fontNames.length === 0) return

    // Build a lookup from font name to URL
    const fontUrlMap = {}
    fonts.forEach(f => { if (f.url) fontUrlMap[f.name] = f.url })

    // Register @font-face for fonts that have valid S3 URLs and haven't been registered yet.
    // Skip Paperclip "missing" placeholders and non-font URLs.
    fontNames.forEach(name => {
      if (registeredFontsRef.current.has(name)) return
      const url = fontUrlMap[name]
      if (url && !url.includes('missing.png') && /\.(ttf|otf|woff2?)\b/i.test(url)) {
        try {
          const face = new FontFace(name, `url(${url})`)
          document.fonts.add(face)
          registeredFontsRef.current.add(name)
        } catch (e) {
          console.warn(`Failed to register font "${name}":`, e)
        }
      }
    })

    const loadPromises = fontNames.map(name =>
      document.fonts.load(`16px "${name}"`).catch(e => {
        console.warn(`Font "${name}" could not be loaded:`, e)
        return []
      })
    )
    Promise.all(loadPromises).then(() => {
      if (stageRef.current) {
        stageRef.current.batchDraw()
      }
      setFontsReady(true)
    })
  }, [fields, fonts])

  // Expose export function to parent for proof generation
  // Composites the background image + Konva text layer into a single image
  // Exports at native template resolution (image_width x image_height) for 300 DPI print quality
  // For very large templates, caps at browser canvas limits (~16384px per side)
  const exportCanvasImage = useCallback((pixelRatio) => {
    var stage = stageRef.current
    var wrapper = canvasWrapperRef.current
    if (!stage) return null

    // Deselect all to hide selection handles/transformer
    setSelectedIds(new Set())
    setEditingField(null)

    // Wait a tick for React to re-render without selection UI
    return new Promise(function(resolve) {
      setTimeout(async function() {
        var ratio = pixelRatio || (template.scaling_factor || 3)
        // Cap to browser canvas limits (~16384px per side, ~268M total pixels)
        var maxDim = 16384
        var maxPixels = 268000000
        var w = dimensions.width * ratio
        var h = dimensions.height * ratio
        if (w > maxDim || h > maxDim || (w * h) > maxPixels) {
          var scale = Math.min(maxDim / w, maxDim / h, Math.sqrt(maxPixels / (w * h)))
          ratio = Math.floor(ratio * scale)
          w = dimensions.width * ratio
          h = dimensions.height * ratio
        }

        // Use PNG (lossless) for large-format prints, JPEG for smaller items
        var isLargeFormat = w >= 3000 || h >= 3000
        var outputMime = isLargeFormat ? 'image/png' : 'image/jpeg'

        // Get the Konva stage as a data URL (text overlay only)
        var stageDataUrl = stage.toDataURL({
          pixelRatio: ratio,
          mimeType: 'image/png',
        })

        // Find the background image URL from the DOM
        var bgImgEl = wrapper ? wrapper.querySelector('img') : null
        if (!bgImgEl || !bgImgEl.src) {
          resolve(stageDataUrl)
          return
        }

        // Load background as a CORS-safe image by proxying through a temp canvas
        // Use XMLHttpRequest with responseType blob to bypass CORS on the fetch
        var xhr = new XMLHttpRequest()
        xhr.open('GET', '/proxy_image?url=' + encodeURIComponent(bgImgEl.src), true)
        xhr.responseType = 'blob'
        xhr.onload = function() {
          if (xhr.status !== 200) {
            resolve(stageDataUrl)
            return
          }
          var bgBlobUrl = URL.createObjectURL(xhr.response)
          var bgImg = new Image()
          bgImg.onload = function() {
            var offscreen = document.createElement('canvas')
            offscreen.width = w
            offscreen.height = h
            var ctx = offscreen.getContext('2d')
            ctx.drawImage(bgImg, 0, 0, w, h)
            URL.revokeObjectURL(bgBlobUrl)

            var overlayImg = new Image()
            overlayImg.onload = function() {
              ctx.drawImage(overlayImg, 0, 0, w, h)
              resolve(isLargeFormat ? offscreen.toDataURL('image/png') : offscreen.toDataURL('image/jpeg', 0.95))
            }
            overlayImg.onerror = function() { resolve(stageDataUrl) }
            overlayImg.src = stageDataUrl
          }
          bgImg.onerror = function() {
            URL.revokeObjectURL(bgBlobUrl)
            resolve(stageDataUrl)
          }
          bgImg.src = bgBlobUrl
        }
        xhr.onerror = function() { resolve(stageDataUrl) }
        xhr.send()
      }, 100)
    })
  }, [template.scaling_factor, dimensions.width, dimensions.height])

  useEffect(() => {
    if (onExportReady) onExportReady(exportCanvasImage)
  }, [onExportReady, exportCanvasImage])

  // Keep editText ref in sync
  useEffect(() => {
    editTextRef.current = editText
  }, [editText])

  // Responsive scaling
  useEffect(() => {
    let rafId = null

    const calculateScale = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (!containerRef.current || dimensions.width <= 0) return

        const parent = containerRef.current.parentElement
        const availableWidth = maxContainerWidth || (parent ? parent.clientWidth - 32 : window.innerWidth - 64)

        let newScale = 1
        if (availableWidth > 0 && dimensions.width > availableWidth) {
          newScale = availableWidth / dimensions.width
        }

        // Also constrain by height if maxContainerHeight is provided
        if (maxContainerHeight > 0 && dimensions.height > 0) {
          const heightScale = maxContainerHeight / dimensions.height
          newScale = Math.min(newScale, heightScale)
        }

        setDisplayScale(prev => Math.abs(prev - newScale) > 0.01 ? newScale : prev)
      })
    }

    const initialTimeout = setTimeout(calculateScale, 100)
    window.addEventListener('resize', calculateScale)

    return () => {
      clearTimeout(initialTimeout)
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', calculateScale)
    }
  }, [dimensions.width, dimensions.height, maxContainerWidth, maxContainerHeight])

  // Update toolbar position when selection changes
  useEffect(() => {
    if (selectedField && canvasWrapperRef.current) {
      const fieldX = selectedField.left || 0
      const fieldY = selectedField.top || 0
      const fieldHeight = selectedField.height || 30

      const positionKey = `${primarySelectedId}-${Math.round(fieldX)}-${Math.round(fieldY)}-${Math.round(fieldHeight)}`
      if (positionKey !== positionedForSelectionRef.current) {
        positionedForSelectionRef.current = positionKey
        const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()
        setToolbarPosition({
          x: wrapperRect.left + (fieldX * effectiveScale),
          y: wrapperRect.top + ((fieldY + fieldHeight) * effectiveScale) + 10
        })
      }
    } else if (selectedIds.size === 0) {
      positionedForSelectionRef.current = null
    }
  }, [primarySelectedId, selectedField?.left, selectedField?.top, selectedField?.height, effectiveScale, selectedIds.size])

  // Handle field change (drag/resize) with group move support
  const handleFieldChange = useCallback((updatedField) => {
    // Check if this is a group drag (multi-select with drag positions recorded)
    if (selectedIds.size > 1 && selectedIds.has(updatedField.id) && dragStartPositions.current[updatedField.id]) {
      const startPos = dragStartPositions.current[updatedField.id]
      const dx = (updatedField.left || 0) - startPos.x
      const dy = (updatedField.top || 0) - startPos.y

      if (dx !== 0 || dy !== 0) {
        setFields(prev => {
          const updated = { ...prev }
          selectedIds.forEach(id => {
            const origPos = dragStartPositions.current[id]
            if (!origPos) return

            if (id === updatedField.id) {
              // The dragged field
              const fieldObj = updatedField.object
              if (fieldObj) {
                fieldObj.x = updatedField.left
                fieldObj.y = updatedField.top
                fieldObj.width = updatedField.width
                fieldObj.height = updatedField.height
                fieldObj.save()
              }
              updated[id] = updatedField
            } else {
              // Other selected fields: apply same delta
              const field = prev[id]
              const newField = {
                ...field,
                left: Math.round(origPos.x + dx),
                top: Math.round(origPos.y + dy)
              }
              if (field.object) {
                field.object.x = newField.left
                field.object.y = newField.top
                field.object.save()
              }
              updated[id] = newField
            }
          })
          return updated
        })
        dragStartPositions.current = {}
        return
      }
    }

    // Normal single-field change
    const fieldObj = updatedField.object
    if (fieldObj) {
      fieldObj.x = updatedField.left
      fieldObj.y = updatedField.top
      fieldObj.width = updatedField.width
      fieldObj.height = updatedField.height
      fieldObj.save()
    }
    setFields(prev => ({ ...prev, [updatedField.id]: updatedField }))
  }, [setFields, selectedIds])

  // Handle toolbar updates (font, color, size, etc.) - applies to ALL selected fields
  const handleToolbarUpdate = useCallback((updates) => {
    if (selectedIds.size === 0) return

    setFields(prev => {
      const updated = { ...prev }
      selectedIds.forEach(id => {
        const field = prev[id]
        if (!field || isImageField(field)) return

        const fieldObj = field.object
        if (fieldObj) {
          if (updates.fontName !== undefined) fieldObj.fontId = updates.fontId
          if (updates.textSize !== undefined) fieldObj.textSize = updates.textSize
          if (updates.color !== undefined) fieldObj.color = updates.color
          if (updates.align !== undefined) fieldObj.align = updates.align
          if (updates.verticalAlign !== undefined) fieldObj.verticalAlign = updates.verticalAlign
          if (updates.wrap !== undefined) fieldObj.wrap = updates.wrap
          if (updates.spacing !== undefined) fieldObj.spacing = updates.spacing
          if (updates.autoFit !== undefined) fieldObj.autoFit = updates.autoFit
          if (updates.minFontSize !== undefined) fieldObj.minFontSize = updates.minFontSize
          if (updates.fontStyle !== undefined) fieldObj.fontStyle = updates.fontStyle
          if (updates.textDecoration !== undefined) fieldObj.textDecoration = updates.textDecoration
          if (updates.customerEditable !== undefined) fieldObj.customerEditable = updates.customerEditable
          if (updates.sampleText !== undefined) fieldObj.sampleText = updates.sampleText
          fieldObj.save()
        }

        updated[id] = { ...prev[id], ...updates }
      })
      return updated
    })
  }, [selectedIds, setFields])

  // Handle copy settings - stores formatting for paste
  const handleCopySettings = useCallback((field) => {
    const formatting = {
      width: field.width,
      height: field.height,
      textSize: field.textSize,
      color: field.color,
      fontName: field.fontName,
      fontId: field.fontId || field.object?.fontId,
      fontStyle: field.fontStyle,
      textDecoration: field.textDecoration,
      align: field.align,
      verticalAlign: field.verticalAlign,
      spacing: field.spacing,
      wrap: field.wrap,
      autoFit: field.autoFit,
      minFontSize: field.minFontSize,
    }
    setCopiedFormatting(formatting)
    showToast('Settings copied! Select another field and click the green paste button')
  }, [showToast])

  // Handle paste settings - applies copied formatting to all selected fields
  const handlePasteSettings = useCallback(() => {
    if (!copiedFormatting || selectedIds.size === 0) return

    setFields(prev => {
      const updated = { ...prev }
      selectedIds.forEach(id => {
        const field = prev[id]
        if (!field || isImageField(field)) return

        const updates = { ...copiedFormatting }
        if (field.object) {
          const fieldObj = field.object
          if (updates.width !== undefined) fieldObj.width = updates.width
          if (updates.height !== undefined) fieldObj.height = updates.height
          if (updates.textSize !== undefined) fieldObj.textSize = updates.textSize
          if (updates.color !== undefined) fieldObj.color = updates.color
          if (updates.fontId !== undefined) fieldObj.fontId = updates.fontId
          if (updates.fontStyle !== undefined) fieldObj.fontStyle = updates.fontStyle
          if (updates.textDecoration !== undefined) fieldObj.textDecoration = updates.textDecoration
          if (updates.align !== undefined) fieldObj.align = updates.align
          if (updates.verticalAlign !== undefined) fieldObj.verticalAlign = updates.verticalAlign
          if (updates.spacing !== undefined) fieldObj.spacing = updates.spacing
          if (updates.wrap !== undefined) fieldObj.wrap = updates.wrap
          if (updates.autoFit !== undefined) fieldObj.autoFit = updates.autoFit
          if (updates.minFontSize !== undefined) fieldObj.minFontSize = updates.minFontSize
          fieldObj.save()
        }
        updated[id] = { ...prev[id], ...updates }
      })
      return updated
    })
    showToast('Formatting applied')
  }, [copiedFormatting, selectedIds, setFields, showToast])

  // Handle selection with shift+click support
  const handleSelect = useCallback((fieldId, e) => {
    const shiftKey = e?.evt?.shiftKey || false
    if (shiftKey) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(fieldId)) next.delete(fieldId)
        else next.add(fieldId)
        return next
      })
    } else {
      setSelectedIds(new Set([fieldId]))
    }
    if (onSelectField) onSelectField(fieldId)
  }, [onSelectField])

  // Handle deselection when clicking on empty area
  const handleStageClick = useCallback((e) => {
    // Deselect unless the click was on a text field's Group (or its children)
    var target = e.target
    var isTextField = false
    var node = target
    while (node) {
      if (node.getClassName && node.getClassName() === 'Group' && node.draggable && node.draggable()) {
        isTextField = true
        break
      }
      node = node.parent
    }
    if (!isTextField && !isSelectingRef.current) {
      setSelectedIds(new Set())
      setEditingField(null)
      if (onSelectField) onSelectField(null)
    }
  }, [onSelectField])

  // Rubber-band selection handlers
  const handleStageMouseDown = useCallback((e) => {
    // Only start rubber-band on Stage background clicks
    if (e.target !== e.target.getStage()) return
    const pos = e.target.getStage().getPointerPosition()
    setSelectionRect({ startX: pos.x, startY: pos.y, x: pos.x, y: pos.y, width: 0, height: 0 })
    isSelectingRef.current = true
  }, [])

  const handleStageMouseMove = useCallback((e) => {
    if (!isSelectingRef.current) return
    const stage = e.target.getStage()
    if (!stage) return
    const pos = stage.getPointerPosition()
    setSelectionRect(prev => {
      if (!prev) return null
      const x = Math.min(prev.startX, pos.x)
      const y = Math.min(prev.startY, pos.y)
      const width = Math.abs(pos.x - prev.startX)
      const height = Math.abs(pos.y - prev.startY)
      return { ...prev, x, y, width, height }
    })
  }, [])

  const handleStageMouseUp = useCallback((e) => {
    if (!isSelectingRef.current) return
    isSelectingRef.current = false

    if (selectionRect && (selectionRect.width > 5 || selectionRect.height > 5)) {
      // Find fields that intersect with the selection rectangle
      const selected = new Set()
      Object.keys(fields).forEach(key => {
        const field = fields[key]
        const fieldLeft = field.left || 0
        const fieldTop = field.top || 0
        const fieldRight = fieldLeft + (field.width || 100)
        const fieldBottom = fieldTop + (field.height || 30)

        if (fieldLeft < selectionRect.x + selectionRect.width &&
            fieldRight > selectionRect.x &&
            fieldTop < selectionRect.y + selectionRect.height &&
            fieldBottom > selectionRect.y) {
          selected.add(key)
        }
      })
      if (selected.size > 0) {
        setSelectedIds(selected)
      }
    }

    setSelectionRect(null)
  }, [selectionRect, fields])

  // Group move: drag start - record positions
  const handleFieldDragStart = useCallback((fieldId) => {
    if (selectedIds.size > 1 && selectedIds.has(fieldId)) {
      dragStartPositions.current = {}
      selectedIds.forEach(id => {
        const f = fields[id]
        if (f) {
          dragStartPositions.current[id] = { x: f.left || 0, y: f.top || 0 }
        }
      })
    } else {
      // Single drag - record for snap
      const f = fields[fieldId]
      if (f) {
        dragStartPositions.current = { [fieldId]: { x: f.left || 0, y: f.top || 0 } }
      }
    }
  }, [selectedIds, fields])

  // Group move + snap guides: drag move
  const handleFieldDragMove = useCallback((fieldId, pos) => {
    const field = fields[fieldId]
    if (!field) return pos

    const fieldWidth = field.width || 100
    const fieldHeight = field.height || 30

    // Snap to canvas center
    const centerX = pos.x + fieldWidth / 2
    const centerY = pos.y + fieldHeight / 2
    const canvasCenterX = dimensions.width / 2
    const canvasCenterY = dimensions.height / 2
    const threshold = 5

    let snappedPos = { ...pos }
    let guides = { vertical: null, horizontal: null }

    if (Math.abs(centerX - canvasCenterX) < threshold) {
      snappedPos.x = canvasCenterX - fieldWidth / 2
      guides.vertical = canvasCenterX
    }
    if (Math.abs(centerY - canvasCenterY) < threshold) {
      snappedPos.y = canvasCenterY - fieldHeight / 2
      guides.horizontal = canvasCenterY
    }

    setSnapGuides(guides)

    // Group move: reposition other selected elements
    if (selectedIds.size > 1 && selectedIds.has(fieldId) && dragStartPositions.current[fieldId]) {
      const startPos = dragStartPositions.current[fieldId]
      const dx = snappedPos.x - startPos.x
      const dy = snappedPos.y - startPos.y

      selectedIds.forEach(id => {
        if (id === fieldId) return
        const ref = fieldGroupRefs.current[id]
        const origPos = dragStartPositions.current[id]
        if (ref && origPos) {
          ref.position({ x: origPos.x + dx, y: origPos.y + dy })
        }
      })
    }

    return snappedPos
  }, [fields, dimensions, selectedIds])

  // Clear snap guides on drag end (called via onChange -> handleFieldChange)
  const handleFieldDragEnd = useCallback(() => {
    setSnapGuides({ vertical: null, horizontal: null })
  }, [])

  // Wrap handleFieldChange to also clear snap guides
  const handleFieldChangeWrapped = useCallback((updatedField) => {
    handleFieldDragEnd()
    handleFieldChange(updatedField)
  }, [handleFieldChange, handleFieldDragEnd])

  // Handle right-click on empty canvas area
  const handleStageContextMenu = useCallback((e) => {
    if (e.target !== e.target.getStage()) return

    e.evt.preventDefault()
    const stage = stageRef.current
    const pointerPos = stage.getPointerPosition()
    const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()

    setContextMenu({
      visible: true,
      x: wrapperRect.left + (pointerPos.x * effectiveScale),
      y: wrapperRect.top + (pointerPos.y * effectiveScale),
      fieldId: null,
      pastePosition: pointerPos
    })
    setSelectedIds(new Set())
  }, [effectiveScale])

  // Handle double-click to edit text
  const handleDoubleClick = useCallback((fieldId) => {
    const field = fields[fieldId]
    if (!field || isImageField(field)) return

    setEditingField(fieldId)
    setEditText(field.sampleText || field.text || '')

    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus()
        textInputRef.current.select()
        textInputRef.current.style.height = 'auto'
        textInputRef.current.style.height = textInputRef.current.scrollHeight + 'px'
      }
    }, 0)
  }, [fields])

  // Handle text edit save
  const handleTextEditSave = useCallback(() => {
    if (!editingField) return

    const currentText = textInputRef.current ? textInputRef.current.value : editTextRef.current
    const field = fields[editingField]

    if (field) {
      if (field.object) {
        if (field.columnType === 'static_text') {
          field.object.text = currentText
        } else {
          field.object.sampleText = currentText
        }
        field.object.save().catch((e) => {
          console.error('Failed to save field text:', e)
        })
        setFields(prev => ({
          ...prev,
          [editingField]: {
            ...prev[editingField],
            text: field.columnType === 'static_text' ? currentText : prev[editingField]?.text,
            sampleText: field.columnType === 'static_text' ? null : currentText,
          }
        }))
      } else {
        setFields(prev => ({
          ...prev,
          [editingField]: { ...prev[editingField], text: currentText, sampleText: null }
        }))
      }
    }

    setEditingField(null)
    setEditText('')
  }, [editingField, fields, setFields])

  // Handle text edit cancel
  const handleTextEditCancel = useCallback(() => {
    setEditingField(null)
    setEditText('')
  }, [])

  // Handle right-click context menu on field
  const handleContextMenu = useCallback((e, fieldId) => {
    e.evt.preventDefault()
    const stage = stageRef.current
    const pointerPos = stage.getPointerPosition()
    const wrapperRect = canvasWrapperRef.current.getBoundingClientRect()

    setContextMenu({
      visible: true,
      x: wrapperRect.left + (pointerPos.x * effectiveScale),
      y: wrapperRect.top + (pointerPos.y * effectiveScale),
      fieldId: fieldId,
      pastePosition: null
    })
    // Add to selection if not already selected
    if (!selectedIds.has(fieldId)) {
      setSelectedIds(new Set([fieldId]))
    }
  }, [effectiveScale, selectedIds])

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, fieldId: null, pastePosition: null })
  }, [])

  // Handle copy field
  const handleCopyField = useCallback(() => {
    if (contextMenu.fieldId && fields[contextMenu.fieldId]) {
      setCopiedField({ ...fields[contextMenu.fieldId] })
      showToast('Copied!')
    }
    closeContextMenu()
  }, [contextMenu.fieldId, fields, closeContextMenu, showToast])

  // Handle paste field
  const handlePasteField = useCallback(async () => {
    const pasteX = contextMenu.pastePosition
      ? Math.round(contextMenu.pastePosition.x)
      : copiedField ? (copiedField.left || 0) + 20 : 100
    const pasteY = contextMenu.pastePosition
      ? Math.round(contextMenu.pastePosition.y)
      : copiedField ? (copiedField.top || 0) + 20 : 100

    // If no copied field, try to create from system clipboard
    if (!copiedField) {
      try {
        const clipboardText = await navigator.clipboard.readText()
        if (clipboardText && clipboardText.trim()) {
          const newField = createTextField(clipboardText.trim(), pasteX, pasteY)
          setFields(prev => ({ ...prev, [newField.id]: newField }))
          setSelectedIds(new Set([newField.id]))
          showToast('Text pasted')
          closeContextMenu()
          return
        }
      } catch (err) {
        console.log('No clipboard text available')
      }
      closeContextMenu()
      return
    }

    // Customer mode: create local-only field
    if (!copiedField.object) {
      const newField = createTextField(
        copiedField.text || copiedField.sampleText || '',
        pasteX,
        pasteY,
        {
          width: copiedField.width,
          height: copiedField.height,
          textSize: copiedField.textSize,
          color: copiedField.color,
          align: copiedField.align,
          verticalAlign: copiedField.verticalAlign,
          spacing: copiedField.spacing,
          wrap: copiedField.wrap,
          fontName: copiedField.fontName,
          fontStyle: copiedField.fontStyle,
          textDecoration: copiedField.textDecoration,
          autoFit: copiedField.autoFit,
          minFontSize: copiedField.minFontSize,
          label: copiedField.label ? `${copiedField.label} (copy)` : 'Custom Text',
          templatePageId: copiedField.templatePageId || null,
          fontId: copiedField.fontId || null,
        }
      )
      setFields(prev => ({ ...prev, [newField.id]: newField }))
      setSelectedIds(new Set([newField.id]))
      showToast('Pasted')
      closeContextMenu()
      return
    }

    // Admin mode: create database-backed field
    const sourceField = copiedField.object
    const Field = (await import('../../../models/Field')).default

    const newField = new Field({
      x: pasteX,
      y: pasteY,
      width: copiedField.width,
      height: copiedField.height,
      align: copiedField.align,
      verticalAlign: copiedField.verticalAlign,
      spacing: copiedField.spacing,
      color: copiedField.color,
      fontId: copiedField.fontId,
      textSize: copiedField.textSize,
      text: copiedField.text,
      sampleText: copiedField.sampleText,
      wrap: copiedField.wrap,
      autoFit: copiedField.autoFit,
      minFontSize: copiedField.minFontSize,
      fontStyle: copiedField.fontStyle,
      textDecoration: copiedField.textDecoration,
      customerEditable: copiedField.customerEditable,
      templateId: template.id,
      columnId: sourceField.columnId,
      templatePageId: copiedField.templatePageId || null,
    })

    closeContextMenu()

    newField.save().then(() => {
      Field.includes(['column', 'font'])
        .find(newField.id)
        .then((res) => {
          setFields(prev => ({
            ...prev,
            [newField.id]: {
              ...copiedField,
              id: newField.id,
              left: pasteX,
              top: pasteY,
              object: res.data
            }
          }))
          setSelectedIds(new Set([newField.id]))
          showToast('Pasted')
        })
    }).catch((e) => {
      console.error('Failed to paste field:', e)
      showToast('Paste failed')
    })
  }, [copiedField, contextMenu.pastePosition, template.id, setFields, closeContextMenu, showToast])

  // Handle delete field (context menu)
  const handleDeleteField = useCallback(() => {
    if (!contextMenu.fieldId || !fields[contextMenu.fieldId]) {
      closeContextMenu()
      return
    }

    const field = fields[contextMenu.fieldId]
    if (field.object) {
      field.object.destroy()
    }

    setFields(prev => {
      const updated = { ...prev }
      delete updated[contextMenu.fieldId]
      return updated
    })
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.delete(contextMenu.fieldId)
      return next
    })
    showToast('Deleted')
    closeContextMenu()
  }, [contextMenu.fieldId, fields, setFields, closeContextMenu, showToast])

  // Handle toggle horizontal/vertical for image fields
  const handleToggleHorizontal = useCallback(() => {
    if (!contextMenu.fieldId || !fields[contextMenu.fieldId]) {
      closeContextMenu()
      return
    }

    const field = fields[contextMenu.fieldId]
    const newHorizontal = !field.horizontal
    const corrected = enforcePhotoRatio(field.width || 80, newHorizontal)
    const updatedField = { ...field, horizontal: newHorizontal, width: corrected.width, height: corrected.height }

    if (field.object) {
      field.object.horizontal = newHorizontal
      field.object.width = corrected.width
      field.object.height = corrected.height
      field.object.save()
    }

    setFields(prev => ({ ...prev, [contextMenu.fieldId]: updatedField }))
    showToast(newHorizontal ? 'Set to horizontal' : 'Set to vertical')
    closeContextMenu()
  }, [contextMenu.fieldId, fields, setFields, closeContextMenu, showToast])

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) closeContextMenu()
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [contextMenu.visible, closeContextMenu])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setUserZoom(prev => {
      const idx = ZOOM_STEPS.findIndex(s => s > prev + 0.01)
      return idx >= 0 ? ZOOM_STEPS[idx] : ZOOM_STEPS[ZOOM_STEPS.length - 1]
    })
  }, [])

  const handleZoomOut = useCallback(() => {
    setUserZoom(prev => {
      // Find last step that's less than current
      for (let i = ZOOM_STEPS.length - 1; i >= 0; i--) {
        if (ZOOM_STEPS[i] < prev - 0.01) return ZOOM_STEPS[i]
      }
      return ZOOM_STEPS[0]
    })
  }, [])

  const handleZoomFit = useCallback(() => {
    setUserZoom(1.0)
  }, [])

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = async (e) => {
      if (editingField) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return

      // Undo: Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
        return
      }

      // Redo: Cmd+Shift+Z or Cmd+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        handleRedo()
        return
      }

      // Select All: Cmd+A
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        setSelectedIds(new Set(Object.keys(fields)))
        return
      }

      // Paste: Cmd+V
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()

        if (copiedField) {
          handlePasteField()
          return
        }

        // No copied field — try system clipboard
        if (selectedIds.size === 1) {
          const id = [...selectedIds][0]
          const field = fields[id]
          if (field && !isImageField(field)) {
            try {
              const clipboardText = await navigator.clipboard.readText()
              if (clipboardText) {
                if (field.object) {
                  if (field.columnType === 'static_text') {
                    field.object.text = clipboardText
                  } else {
                    field.object.sampleText = clipboardText
                  }
                  field.object.save()
                  setFields(prev => ({
                    ...prev,
                    [id]: {
                      ...prev[id],
                      ...(field.columnType === 'static_text' ? { text: clipboardText } : { sampleText: clipboardText })
                    }
                  }))
                } else {
                  setFields(prev => ({
                    ...prev,
                    [id]: { ...prev[id], text: clipboardText, sampleText: null }
                  }))
                }
                showToast('Text pasted')
                return
              }
            } catch (err) {
              console.log('Clipboard access not available')
            }
          }
        }

        // No field selected and no copied field - create new text element from clipboard
        try {
          const clipboardText = await navigator.clipboard.readText()
          if (clipboardText && clipboardText.trim()) {
            const newField = createTextField(clipboardText.trim(), 100, 100)
            setFields(prev => ({ ...prev, [newField.id]: newField }))
            setSelectedIds(new Set([newField.id]))
            showToast('Text pasted')
          }
        } catch (err) {
          console.log('Clipboard access not available')
        }
        return
      }

      // Below shortcuts require at least one selected field
      if (selectedIds.size === 0) return

      // Copy: Cmd+C (copies first selected)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const id = [...selectedIds][0]
        if (fields[id]) {
          setCopiedField({ ...fields[id] })
          showToast('Copied!')
        }
        return
      }

      // Arrow keys - nudge all selected elements
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault()
        const nudgeAmount = e.shiftKey ? 10 : 1
        let deltaX = 0, deltaY = 0

        switch (e.key) {
          case 'ArrowUp': deltaY = -nudgeAmount; break
          case 'ArrowDown': deltaY = nudgeAmount; break
          case 'ArrowLeft': deltaX = -nudgeAmount; break
          case 'ArrowRight': deltaX = nudgeAmount; break
        }

        setFields(prev => {
          const updated = { ...prev }
          selectedIds.forEach(id => {
            const field = prev[id]
            if (!field) return
            const newField = {
              ...field,
              left: (field.left || 0) + deltaX,
              top: (field.top || 0) + deltaY,
            }
            if (field.object) {
              field.object.x = newField.left
              field.object.y = newField.top
              field.object.save()
            }
            updated[id] = newField
          })
          return updated
        })
        return
      }

      // Delete/Backspace - delete all selected fields
      if (e.key === 'Delete' || e.key === 'Backspace') {
        setFields(prev => {
          const updated = { ...prev }
          selectedIds.forEach(id => {
            const field = prev[id]
            if (field?.object) {
              field.object.destroy()
            }
            delete updated[id]
          })
          return updated
        })
        setSelectedIds(new Set())
        showToast(selectedIds.size > 1 ? `Deleted ${selectedIds.size} fields` : 'Deleted')
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [editingField, selectedIds, fields, setFields, handleUndo, handleRedo, showToast, copiedField, handlePasteField])

  // Deselect when clicking outside the canvas
  useEffect(() => {
    var handleOutsideClick = function(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelectedIds(new Set())
        setEditingField(null)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return function() { document.removeEventListener('mousedown', handleOutsideClick) }
  }, [])

  // Safety zone margin: 0.25" at 300 DPI = 75px at native resolution, then divide by scaling factor
  const safetyMargin = 75 / (template.scaling_factor || 1)

  // Check if any user-placed element overflows outside the safety zone
  useEffect(() => {
    if (!onOverflowChange || !dimensions.width) return
    var fieldArray = Object.values(fields || {})
    var hasOverflow = fieldArray.some(function(f) {
      if (!f || f.hidden) return false
      // Only check fields the customer can edit or move
      if (!f.customerEditable) return false
      var fx = f.x || 0
      var fy = f.y || 0
      var fw = f.width || 0
      var fh = f.height || 0
      return fx < safetyMargin || fy < safetyMargin ||
        (fx + fw) > (dimensions.width - safetyMargin) ||
        (fy + fh) > (dimensions.height - safetyMargin)
    })
    onOverflowChange(hasOverflow)
  }, [fields, safetyMargin, dimensions, onOverflowChange])

  // Calculate scaled dimensions for layout
  const scaledWidth = Math.round(dimensions.width * effectiveScale)
  const scaledHeight = Math.round(dimensions.height * effectiveScale)

  return (
    <div ref={containerRef} className={styles.canvasContainer}>
      {/* Fixed toolbar for customer mode */}
      {showFixedToolbar && (
        <FixedToolbar
          field={selectedField}
          selectedCount={selectedIds.size}
          onUpdate={handleToolbarUpdate}
          fonts={fonts}
          disabled={selectedIds.size === 0 || (selectedField && isImageField(selectedField))}
          onSpellCheck={onSpellCheck}
          onAddText={onAddText}
        />
      )}

      {/* Scrollable wrapper for zoom */}
      <div
        className={styles.zoomWrapper}
        style={userZoom > 1 ? { overflow: 'auto', maxHeight: maxContainerHeight || undefined } : undefined}
      >
        {/* Layout wrapper */}
        <div
          className={styles.layoutWrapper}
          style={{ width: scaledWidth, height: scaledHeight, position: 'relative' }}
        >
          {/* Canvas wrapper with transform scale */}
          <div
            ref={canvasWrapperRef}
            className={styles.canvasWrapper}
            style={{
              width: dimensions.width,
              height: dimensions.height,
              transform: `scale(${effectiveScale})`,
              transformOrigin: 'top left',
            }}
          >
            <img
              src={imageUrl}
              alt="Template background"
              className={styles.backgroundImage}
              style={{ width: dimensions.width, height: dimensions.height }}
            />
            <Stage
              ref={stageRef}
              width={dimensions.width}
              height={dimensions.height}
              onClick={handleStageClick}
              onTap={handleStageClick}
              onContextMenu={handleStageContextMenu}
              onMouseDown={handleStageMouseDown}
              onMouseMove={handleStageMouseMove}
              onMouseUp={handleStageMouseUp}
              className={styles.konvaStage}
            >
              <Layer>
                {Object.keys(fields).map((key) => {
                  const field = fields[key]
                  if (isImageField(field)) {
                    return (
                      <TransformableImage
                        key={key}
                        field={field}
                        isSelected={selectedIds.has(key)}
                        onSelect={(e) => handleSelect(key, e)}
                        onChange={handleFieldChangeWrapped}
                        onContextMenu={(e) => handleContextMenu(e, key)}
                        onDragStart={handleFieldDragStart}
                        onDragMove={handleFieldDragMove}
                        registerRef={(ref) => { fieldGroupRefs.current[key] = ref }}
                        hideBoundingBoxes={hideBoundingBoxes}
                      />
                    )
                  }
                  return (
                    <TransformableText
                      key={key + (fontsReady ? '_fontsReady' : '')}
                      field={field}
                      isSelected={selectedIds.has(key)}
                      onSelect={(e) => handleSelect(key, e)}
                      onChange={handleFieldChangeWrapped}
                      onDoubleClick={() => handleDoubleClick(key)}
                      onContextMenu={(e) => handleContextMenu(e, key)}
                      onDragStart={handleFieldDragStart}
                      onDragMove={handleFieldDragMove}
                      registerRef={(ref) => { fieldGroupRefs.current[key] = ref }}
                      hideBoundingBoxes={hideBoundingBoxes}
                    />
                  )
                })}

                {/* Safety zone / print margin */}
                {showSafetyZone && (
                  <Rect
                    x={safetyMargin}
                    y={safetyMargin}
                    width={dimensions.width - 2 * safetyMargin}
                    height={dimensions.height - 2 * safetyMargin}
                    stroke="orange"
                    strokeWidth={1}
                    dash={[8, 4]}
                    listening={false}
                  />
                )}

                {/* Snap guide lines */}
                {snapGuides.vertical !== null && (
                  <Line
                    points={[snapGuides.vertical, 0, snapGuides.vertical, dimensions.height]}
                    stroke="lime"
                    strokeWidth={1}
                    dash={[6, 3]}
                    listening={false}
                  />
                )}
                {snapGuides.horizontal !== null && (
                  <Line
                    points={[0, snapGuides.horizontal, dimensions.width, snapGuides.horizontal]}
                    stroke="lime"
                    strokeWidth={1}
                    dash={[6, 3]}
                    listening={false}
                  />
                )}

                {/* Rubber-band selection rectangle */}
                {selectionRect && selectionRect.width > 0 && (
                  <Rect
                    x={selectionRect.x}
                    y={selectionRect.y}
                    width={selectionRect.width}
                    height={selectionRect.height}
                    fill="rgba(66,133,244,0.15)"
                    stroke="#4285f4"
                    strokeWidth={1}
                    listening={false}
                  />
                )}
              </Layer>
            </Stage>

            {/* Inline text editing */}
            {editingField && fields[editingField] && (
              <textarea
                ref={textInputRef}
                value={editText}
                onChange={(e) => {
                  setEditText(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = e.target.scrollHeight + 'px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleTextEditCancel()
                }}
                onBlur={handleTextEditSave}
                className={styles.inlineTextEdit}
                style={{
                  position: 'absolute',
                  left: fields[editingField].left || 0,
                  top: fields[editingField].top || 0,
                  width: fields[editingField].width || 100,
                  minHeight: fields[editingField].height || 30,
                  fontSize: fields[editingField].textSize || 16,
                  fontFamily: fields[editingField].fontName || 'Arial',
                  color: fields[editingField].color || '#000000',
                  textAlign: fields[editingField].align || 'left',
                  zIndex: 100,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Zoom controls */}
      {showZoomControls && (
        <div className={styles.zoomControls}>
          <button type="button" className={styles.zoomBtn} onClick={handleZoomOut} title="Zoom out">−</button>
          <span className={styles.zoomLabel}>{Math.round(effectiveScale * 100)}%</span>
          <button type="button" className={styles.zoomBtn} onClick={handleZoomIn} title="Zoom in">+</button>
          <button type="button" className={styles.zoomFitBtn} onClick={handleZoomFit} title="Fit to screen">Fit</button>
        </div>
      )}

      {/* Floating toolbar (admin mode) */}
      {!showFixedToolbar && selectedField && !isImageField(selectedField) && !contextMenu.visible && (
        <FloatingToolbar
          field={selectedField}
          onUpdate={handleToolbarUpdate}
          onCopySettings={handleCopySettings}
          onPasteSettings={handlePasteSettings}
          hasCopiedFormatting={!!copiedFormatting}
          position={toolbarPosition}
          fonts={fonts}
        />
      )}

      {/* Context menu */}
      {contextMenu.visible && (
        <div
          className={styles.contextMenu}
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 160),
            top: Math.min(contextMenu.y, window.innerHeight - 120)
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.fieldId ? (
            <>
              <button onClick={handleCopyField}><span>📋</span> Copy</button>
              <button onClick={handlePasteField}><span>📄</span> Paste</button>
              {contextMenu.fieldId && fields[contextMenu.fieldId] && isImageField(fields[contextMenu.fieldId]) && !isQrField(fields[contextMenu.fieldId]) && !isLogoField(fields[contextMenu.fieldId]) && (
                <>
                  <hr />
                  <button onClick={handleToggleHorizontal}>
                    <span>{fields[contextMenu.fieldId].horizontal ? '↕' : '↔'}</span>
                    {fields[contextMenu.fieldId].horizontal ? ' Make Vertical' : ' Make Horizontal'}
                  </button>
                </>
              )}
              <hr />
              <button onClick={handleDeleteField} className={styles.deleteBtn}>
                <span>🗑️</span> Delete
              </button>
            </>
          ) : (
            <button onClick={handlePasteField}><span>📄</span> Paste</button>
          )}
        </div>
      )}

      {/* Toast notification */}
      {toast.visible && (
        <div className={styles.toast}>{toast.message}</div>
      )}

      {showControls && (
        <>
          <div className={styles.controls}>
            <button
              type="button"
              className={styles.undoRedoBtn}
              onClick={handleUndo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
            <button
              type="button"
              className={styles.undoRedoBtn}
              onClick={handleRedo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
            >
              Redo
            </button>
            <span className={styles.hintText}>Shift + Enter for a new line</span>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideBoundingBoxes}
                onChange={(e) => setHideBoundingBoxes(e.target.checked)}
              />
              <span>Hide bounding boxes</span>
            </label>
          </div>
          <p className={styles.helpText}>
            <small>Click to select, drag to move, arrows to nudge. Double-click text to edit. Shift+click or drag to multi-select. Ctrl+Z / Cmd+Z to undo.</small>
          </p>
        </>
      )}
    </div>
  )
}
